import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Search, Star, MapPin, Sparkles, ChevronRight, Building2,
  Factory, Sprout, Package, Truck, Handshake, Route, Palette,
  Megaphone, Scale, FlaskConical, ShieldCheck, Calculator, Filter, X,
} from "lucide-react";

const CATEGORY_ICONS = {
  "co-manufacturers": Factory,
  "ingredients": Sprout,
  "packaging": Package,
  "freight-3pl": Truck,
  "brokers": Handshake,
  "distributors": Route,
  "design-branding": Palette,
  "pr-marketing": Megaphone,
  "legal": Scale,
  "rd-labs": FlaskConical,
  "insurance": ShieldCheck,
  "finance": Calculator,
};

const SORT_OPTIONS = [
  { value: "featured", label: "Featured first" },
  { value: "rating", label: "Highest rated" },
  { value: "recent", label: "Recently added" },
  { value: "name", label: "A → Z" },
];

export default function IndustryDirectory() {
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/industry/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (category !== "all") params.category = category;
    api.get("/industry/vendors", { params })
      .then((r) => setVendors(r.data))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  }, [q, category, sort]);

  const totalVendors = useMemo(
    () => categories.reduce((sum, c) => sum + (c.vendor_count || 0), 0),
    [categories],
  );

  return (
    <AppShell title="Industry Directory">
      <div className="w-full">
        <PageHero
          eyebrow="New — Industry Directory"
          icon={Sparkles}
          title={<>Every vendor a CPG founder <span className="text-grad">needs</span></>}
          subtitle="Search co-manufacturers, packaging, freight, brokers, distributors, agencies, legal, R&D labs, insurance, and fractional CFOs. Read reviews from other Institute founders and check public references before you sign anything."
          testid="industry-hero"
        />

        {/* Search + sort bar */}
        <div className="mt-8 card-elevated p-4 flex flex-col md:flex-row gap-3">
          <label className="flex-1 flex items-center gap-2 rounded-full bg-shell-border/20 px-4 py-2.5">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, service, or specialty…"
              className="flex-1 bg-transparent text-sm focus:outline-none"
              data-testid="industry-search"
            />
            {q && (
              <button onClick={() => setQ("")} aria-label="Clear search" className="text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </label>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="industry-sort"
              className="rounded-full bg-white border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Category chips */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="industry-category-chips">
          <CategoryChip
            active={category === "all"}
            onClick={() => setCategory("all")}
            label="All"
            count={totalVendors}
            icon={Building2}
            testid="industry-category-all"
          />
          {categories.map((c) => (
            <CategoryChip
              key={c.slug}
              active={category === c.slug}
              onClick={() => setCategory(c.slug)}
              label={c.label}
              count={c.vendor_count}
              icon={CATEGORY_ICONS[c.slug] || Building2}
              testid={`industry-category-${c.slug}`}
            />
          ))}
        </div>

        {/* Results grid */}
        <div className="mt-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading directory…</p>
          ) : vendors.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="industry-empty">
              No vendors matched. Try a broader search or clear the category filter.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="industry-vendor-grid">
              {vendors.map((v) => (
                <VendorCard key={v.slug} vendor={v} categories={categories} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function CategoryChip({ active, onClick, label, count, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold border transition",
        active
          ? "text-white shadow-sm border-transparent"
          : "text-foreground/80 bg-white border-shell-border hover:border-primary/40",
      ].join(" ")}
      style={active ? { background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)" } : undefined}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {typeof count === "number" && (
        <span className={active ? "text-white/80 text-[11px]" : "text-muted-foreground text-[11px]"}>
          {count}
        </span>
      )}
    </button>
  );
}

function VendorCard({ vendor, categories }) {
  const cat = categories.find((c) => c.slug === vendor.category);
  const Icon = CATEGORY_ICONS[vendor.category] || Building2;
  const stars = Number(vendor.avg_member_rating || 0);
  const reviews = Number(vendor.member_review_count || 0);
  const loc = vendor.location || {};
  const locLabel = [loc.city, loc.state || loc.country].filter(Boolean).join(", ");
  return (
    <Link
      to={`/industry-directory/${vendor.slug}`}
      className="card-elevated p-5 group hover:-translate-y-0.5 hover:shadow-lg transition"
      data-testid={`industry-vendor-card-${vendor.slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <div className="flex items-center gap-1.5">
          {vendor.featured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 text-pink-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
              <Star className="w-2.5 h-2.5 fill-current" /> Featured
            </span>
          )}
          {vendor.demo && (
            <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
              Demo
            </span>
          )}
        </div>
      </div>
      <p className="mt-4 font-display text-lg leading-tight" data-testid={`industry-vendor-name-${vendor.slug}`}>{vendor.name}</p>
      {vendor.subcategory && (
        <p className="mt-0.5 text-xs uppercase tracking-widest text-muted-foreground">{vendor.subcategory}</p>
      )}
      <p className="mt-2 text-[13px] text-muted-foreground line-clamp-2">{vendor.description}</p>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px] text-muted-foreground">
        {locLabel && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {locLabel}
          </span>
        )}
        {cat?.label && (
          <span>{cat.label}</span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <StarRow rating={stars} reviews={reviews} />
        <span className="text-[11.5px] font-semibold text-primary inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
          View <ChevronRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

export function StarRow({ rating, reviews, size = 14 }) {
  const rounded = Math.round(rating * 2) / 2; // nearest half
  return (
    <div className="inline-flex items-center gap-1" data-testid="industry-star-row">
      <div className="flex" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((n) => {
          const fill = rounded >= n ? "full" : rounded >= n - 0.5 ? "half" : "empty";
          return (
            <Star
              key={n}
              style={{ width: size, height: size }}
              className={
                fill === "empty"
                  ? "text-slate-300"
                  : "text-pink-500 fill-current"
              }
            />
          );
        })}
      </div>
      <span className="text-[11.5px] text-muted-foreground">
        {reviews > 0 ? `${rating.toFixed(1)} · ${reviews} ${reviews === 1 ? "review" : "reviews"}` : "No reviews yet"}
      </span>
    </div>
  );
}
